import { useCallback, useMemo, useRef, useState } from "react";
import { CellKeyDownEvent, ColDef, ColumnState, RowClassParams, RowClickedEvent, RowDoubleClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import useUiuxStore from "src/store/useUiuxStore";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";

import { aggridHeaderClass, commonBorderString } from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { showWarning } from "src/utils/toast/toastUtil";

// 새로운 구조 import
import { SearchableDto, CodeType } from "src/types/base";
import { getUiConfig, getApiConfig } from "src/utils/categoryUtils";
import { useGridCellSelection } from "src/utils/grid/GridHandlerContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { createSaveColumnState, onGridReady } from "src/utils/grid/gridStyleUtils";
import { useDescText } from "src/hooks/useDescText";
import { DESC_FLD } from "src/utils/descLanguage/descLanguageUtil";

// ColumnConfig 인터페이스 정의
interface ColumnConfig {
  headerName: string;
  field: string;
  flex?: number;
  width?: number;
  cellRenderer?: string;
  rendererConfig?: {
    type: 'commonCode';
    listKey: string;
    codeField?: string;
    nameField?: string;
  };
}


interface SearchTableProps<T extends SearchableDto> {
	codeType: CodeType;
	formName?: string;
	data: T[];
	selected: T | undefined;
	setSelected: (item: T | undefined) => void;
	onConfirm?: (data: T) => void;
	onCloseConfirm?: (data: T) => void;
	processType_focus?: string;
}

// 이번 다국어 적용 대상 팝업만 field와 commentCd를 연결한다.
// field/colId는 조회 데이터와 저장된 컬럼 상태의 식별자이므로 변경하지 않고 headerName만 번역한다.
const getColumnCommentCd = (codeType: CodeType, field: string): string | undefined => {
	// 대상 밖의 공통 코드 검색은 기존 categories.ts의 headerName을 그대로 유지한다.
	if (!['productOrder', 'productResult', 'employee'].includes(codeType)) return undefined;

	switch (field) {
		case 'rowIndex':
			return DESC_FLD.SEQUENCE;
		case 'productOrderNo':
			return codeType === 'productOrder'
				? DESC_FLD.WORK_ORDER_NO
				: DESC_FLD.PRODUCTION_ORDER_NO;
		case 'orderDt':
			return DESC_FLD.WORK_DATE;
		case 'productDt':
			return DESC_FLD.PRODUCTION_DATE;
		case 'productResultNo':
			return DESC_FLD.PRODUCTION_RESULT_NO;
		case 'compCd':
			return DESC_FLD.BUSINESS_SITE;
		case 'workplaceCd':
		case 'workplaceNm':
			return DESC_FLD.WORK_PROCESS;
		case 'itemCd':
			return DESC_FLD.ITEM_CODE;
		case 'itemNm':
			return DESC_FLD.ITEM_NAME;
		case 'itemSpec':
			return DESC_FLD.SPECIFICATION;
		case 'orderQty':
			return DESC_FLD.ORDER_QTY;
		case 'resultQty':
		case 'acceptQty':
			return DESC_FLD.ACTUAL_QTY;
		case 'productEmp':
			return DESC_FLD.WORKER;
		case 'productQty':
			return DESC_FLD.PRODUCTION_QTY;
		case 'badQty':
			return DESC_FLD.DEFECT_QTY;
		case 'empNo':
			return DESC_FLD.EMPLOYEE_NO;
		case 'empNm':
			return DESC_FLD.EMPLOYEE_NAME;
		case 'closeTag':
			return DESC_FLD.CLOSE_STATUS;
		default:
			return undefined;
	}
};

export const SearchTable = <T extends SearchableDto>(props: SearchTableProps<T>) => {
	const { 
		codeType,
		formName,
		data,
		selected,
		setSelected,
		onConfirm = () => { alert('모달 코드 등록후 행동할 동작이 없습니다.'); },
		onCloseConfirm = () => { /* alert('모달 코드 등록후 행동할 동작이 없습니다.'); */ },
		processType_focus = 'normal',
	} = props;

	const {  setCodeSearchModalVisible } = useUiuxStore();
	const commonCodeStore = useCommonCodeStore();
	const { renderDescText } = useDescText();
	const gridRef = useRef<AgGridReact>(null);
	const [searchColumnState, setSearchColumnState] = useState<ColumnState[] | null>(null);
	const columnStateKey = `CodeSearchModal-${codeType}-column-state`;

	// 설정에서 컬럼 정의와 코드 필드 가져오기 (새로운 구조)
	const config = useMemo(() => {
		// 공통코드 스토어 값을 전달하여 동적 컬럼 생성
		const storeData = { 
			unitList: commonCodeStore.unitList,
			itemAccountList: commonCodeStore.itemAccountList
		};
		const uiConfig = getUiConfig(codeType, storeData);
		const apiConfig = getApiConfig(codeType);
		
		if (!uiConfig || !apiConfig) {
			console.warn(`Missing config for codeType: ${codeType}`);
			return { columns: [], codeField: '', nameField: '' };
		}
		
		return {
			columns: uiConfig.columns,
			codeField: apiConfig.codeField,
			nameField: apiConfig.nameField,
			emptyMessage: uiConfig.emptyMessage
		};
	}, [codeType, commonCodeStore.unitList, commonCodeStore.itemAccountList]);

	// 범용 공통코드 렌더러 생성 함수
	const createCommonCodeRenderer = useCallback((
		list: any[], 
		fieldName: string, 
		codeField: string = 'minorCd', 
		nameField: string = 'codeNm'
	) => {
		return (params: any) => {
			if (!params.data) return '';
			const codeValue = params.data[fieldName];
			if (codeValue === null || codeValue === undefined) return '';
			
			const foundItem = list.find((item: any) => item[codeField] === codeValue);
			return foundItem ? foundItem[nameField] : codeValue;
		};
	}, []);

	// 특별한 셀 렌더러 생성 함수 (방안 3: rendererConfig 기반)
	const createCellRenderer = useCallback((rendererType: string, rendererConfig?: ColumnConfig['rendererConfig'], fieldName?: string) => {
		// 범용 공통코드 렌더러 처리
		if (rendererType === 'commonCodeRenderer' && rendererConfig && fieldName) {
			const { listKey, codeField = 'minorCd', nameField = 'codeNm' } = rendererConfig;
			const list = (commonCodeStore as any)[listKey] || [];
			
			if (list.length > 0) {
				return createCommonCodeRenderer(list, fieldName, codeField, nameField);
			}
		}
		
		// 기타 특수 렌더러는 여기에 추가
		return undefined;
	}, [commonCodeStore, createCommonCodeRenderer]);

	// 컬럼 정의 생성: 업무 필드와 컬럼 상태 키는 유지하고 화면에 보이는 헤더만 현재 언어로 변환한다.
	const columnDefs: ColDef<any>[] = useMemo(() => {
		return config.columns.map((col: ColumnConfig) => {
			const commentCd = getColumnCommentCd(codeType, col.field);
			const colDef: ColDef<any> = {
				// 매핑 코드가 없거나 번역 데이터가 없으면 categories.ts의 한국어 headerName을 fallback으로 사용한다.
				headerName: commentCd ? renderDescText(commentCd, col.headerName) : col.headerName,
				field: col.field,
				colId: col.field,
				cellStyle: { borderRight: commonBorderString },
				headerClass: aggridHeaderClass
			};

			// 순번 컬럼 특별 처리
			if (col.field === 'rowIndex') {
				colDef.valueGetter = 'node.rowIndex + 1';
				colDef.width = col.width;
			} else {
				// 우클릭 너비 맞춤이 적용되도록 flex 컬럼도 초기 width로 변환
				if (col.flex) {
					colDef.width = col.width ?? 140;
					colDef.minWidth = 50;
				} else if (col.width) {
					colDef.width = col.width;
				}
			}

			// 특별한 렌더러 설정 (방안 3: rendererConfig 기반)
			if (col.cellRenderer) {
				colDef.cellRenderer = createCellRenderer(col.cellRenderer, col.rendererConfig, col.field);
			}

			return colDef;
		});
	}, [codeType, config.columns, createCellRenderer, renderDescText]);

	const restoredColumnDefs = useMemo(() => GridCustomColumnDefs({
		baseColumnDefs: columnDefs,
		columnState: searchColumnState,
		headerNames: Object.fromEntries(
			columnDefs.map((column) => [column.colId ?? '', column.headerName ?? ''])
		),
	}), [columnDefs, searchColumnState]);

	const handleGridReady = useCallback(() => {
		onGridReady(gridRef, columnStateKey, setSearchColumnState);
	}, [columnStateKey]);

	const saveColumnState = useMemo(
		() => createSaveColumnState(gridRef, columnStateKey, setSearchColumnState),
		[columnStateKey]
	);

	// 드래그 범위 선택 + Ctrl+C 복사 훅
	const {
		handleCellMouseDown,
		handleCellMouseOver,
		handleCellKeyDown: handleRangeCopyKeyDown,
		processedColDefs,
	} = useGridCellSelection({ columnDefs: restoredColumnDefs });

	// 그리드 데이터 생성 (필요한 필드만 추출)
	const gridData = useMemo(() => {
		return data.map((item) => {
			const gridItem: any = {};
			config.columns.forEach((col: ColumnConfig) => {
				if (col.field !== 'rowIndex') {
					gridItem[col.field] = (item as any)[col.field];
				}
			});
			return gridItem;
		});
	}, [data, config.columns]);

    // codeField가 단일 키이거나 복합키인 경우를 하나의 방식으로 처리하는 행 식별
	const codeFields = useMemo(
		() => Array.isArray(config.codeField) ? config.codeField : [config.codeField],
		[config.codeField]
	);

    // 행을 식별하는 데 필요한 키가 모두 들어 있는지 검사합니다.
	const hasCodeFieldValues = useCallback((rowData: unknown): boolean => {
		if (!rowData) return false;
		const record = rowData as Record<string, unknown>;
		return codeFields.every((field) => {
			const value = record[field];
			return value !== undefined && value !== null && value !== '';
		});
	}, [codeFields]);

    // 두 행의 식별 키가 모두 같은지 확인
	const isSameRow = useCallback((left: unknown, right: unknown): boolean => {
		if (!hasCodeFieldValues(left) || !hasCodeFieldValues(right)) return false;
		const leftData = left as Record<string, unknown>;
		const rightData = right as Record<string, unknown>;
		return codeFields.every((field) => leftData[field] === rightData[field]);
	}, [codeFields, hasCodeFieldValues]);

    // 그리드에서 클릭한 행과 식별 키가 동일한 원본 데이터를 data 배열에서 찾아 반환
	const findItemByRowData = useCallback((rowData: unknown): T | undefined => {
		return data.find((item) => isSameRow(item, rowData));
	}, [data, isSameRow]);

	// 행 클릭 핸들러 (함수형 업데이트 사용)
	const handleRowClick = useCallback((e: RowClickedEvent<any>) => {
		const foundItem = findItemByRowData(e.data);
		if (!foundItem) return;

		if (isSameRow(selected, foundItem)) {
			setSelected(undefined);
		} else {
			setSelected(foundItem);
		}
	}, [findItemByRowData, isSameRow, selected, setSelected]);

	/** 더블클릭·엔터 공통: 원본 data 행으로 확정 후 모달 닫기 */
	const confirmRow = useCallback(
		(rowData: unknown) => {
			const searchedItem = findItemByRowData(rowData);

			if (!searchedItem) {
				showWarning(`${codeFields.join(', ')}이(가) 존재하지 않습니다.`);
				return;
			}

			if (onConfirm) onConfirm(searchedItem);
			setCodeSearchModalVisible(false);

			if (onCloseConfirm) onCloseConfirm(searchedItem as T);
		},
		[codeFields, findItemByRowData, onConfirm, setCodeSearchModalVisible, onCloseConfirm]
	);

	const handleRowDoubleClick = useCallback(
		(e: RowDoubleClickedEvent<any>) => {
			confirmRow(e.data);
		},
		[confirmRow]
	);

	/** Ctrl+C: 범위 복사 / Enter: 행 확정 */
	const handleCellKeyDown = useCallback(
		(e: CellKeyDownEvent<any>) => {
			const ev = e.event as KeyboardEvent | undefined | null;
			if (!ev) return;

			// Ctrl+C → 드래그 선택 범위를 클립보드에 복사
			if ((ev.ctrlKey || ev.metaKey) && ev.key === 'c') {
				handleRangeCopyKeyDown(e);
				return;
			}

			// Enter → 행 확정 (기존 로직)
			if (ev.key !== 'Enter' || ev.ctrlKey || ev.altKey || ev.metaKey) return;
			const rowData = e.node?.data;
			if (!hasCodeFieldValues(rowData)) return;
			ev.preventDefault();
			ev.stopPropagation();
			confirmRow(rowData);
		},
		[handleRangeCopyKeyDown, hasCodeFieldValues, confirmRow]
	);

	// 행 클래스 규칙
	const rowClassRules: Record<string, (params: RowClassParams<any>) => boolean> = useMemo(() => {
		return {
			selected: (params) => isSameRow(params.data, selected),
			hover: (params) => !isSameRow(params.data, selected),
		};
	}, [isSameRow, selected]);

	const defaultColDef = useMemo<ColDef<any>>(() => ({
		sortable: true,
		resizable: true,
		cellClass: 'border-r-2 border-gray-300 line-height-0-5',
		hide: false,
	}), []);

	return (
		<GridContextWrapper<any>
			gridRef={gridRef}
			paddedGridData={gridData}
			initSelected={() => setSelected(undefined)}
			defaultRowData={{}}
			baseColumnDefs={columnDefs}
			setColumnState={setSearchColumnState}
			localStorageKey={columnStateKey}
			contextMenuZIndex={10080}
		>
			<AgGridReact
				key={codeType}
				ref={gridRef}
				rowData={gridData}
				columnDefs={processedColDefs}
				enableRangeSelection={true}
				// AG Grid 빈 결과 영역도 일반 DOM과 같은 commentCd/fallback 규칙으로 표시한다.
				overlayNoRowsTemplate={`<span style="padding: 32px;">${renderDescText(DESC_FLD.NO_DATA, config.emptyMessage ?? '조회된 데이터가 없습니다.')}</span>`}
				suppressScrollOnNewData={true}
				suppressColumnVirtualisation={true}
				defaultColDef={defaultColDef}
				onRowClicked={handleRowClick}
				onRowDoubleClicked={handleRowDoubleClick}
				onCellMouseDown={handleCellMouseDown}
				onCellMouseOver={handleCellMouseOver}
				onCellKeyDown={handleCellKeyDown}
				onGridReady={handleGridReady}
				onColumnResized={saveColumnState}
				rowClassRules={rowClassRules}
				headerHeight={35}
				rowHeight={35}
			/>
		</GridContextWrapper>
	);
};
